import { Conversation, ConversationKind } from '../types/Conversation';
import { getCustomListIdFromNavSection, NavSection } from '../types/conversationNav';

export type ConversationClassificationSource = Pick<Conversation, 'participants' | 'hasExternalParticipant' | 'conversationKind' | 'customListId'>;

export const getConversationKind = (conversation: ConversationClassificationSource): ConversationKind => {
  if (conversation.conversationKind) {
    return conversation.conversationKind;
  }

  const participantCount = conversation.participants.filter(
    (participant) => participant.type !== 'system' && participant.type !== 'aiAssistant',
  ).length;
  const hasExternalParticipant = Boolean(conversation.hasExternalParticipant);

  if (participantCount === 1 && hasExternalParticipant) {
    return 'external-direct';
  }

  if (participantCount === 2) {
    return hasExternalParticipant ? 'external-direct' : 'internal-direct';
  }

  if (participantCount >= 3) {
    return hasExternalParticipant ? 'external-channel' : 'internal-channel';
  }

  return 'unknown';
};

export const isDirectConversation = (conversation: ConversationClassificationSource): boolean => {
  const kind = getConversationKind(conversation);
  return kind === 'internal-direct' || kind === 'external-direct';
};

export const isAiAssistantDirectConversation = (conversation: ConversationClassificationSource): boolean => {
  return isDirectConversation(conversation)
    && conversation.participants.some((participant) => participant.type === 'aiAssistant');
};

export const isEmployeeAiAssistantDirectConversation = (conversation: ConversationClassificationSource): boolean => {
  const companyMemberCount = conversation.participants.filter((participant) => participant.type === 'companyMember').length;
  const hasAiAssistant = conversation.participants.some((participant) => participant.type === 'aiAssistant');
  const hasExternalParticipant = Boolean(conversation.hasExternalParticipant)
    || conversation.participants.some((participant) => participant.type === 'customer' || participant.type === 'lead');

  return hasAiAssistant && !hasExternalParticipant && companyMemberCount === 1;
};

export const isChannelConversation = (conversation: ConversationClassificationSource): boolean => {
  const kind = getConversationKind(conversation);
  return kind === 'internal-channel' || kind === 'external-channel';
};

export const isExternalConversation = (conversation: ConversationClassificationSource): boolean => {
  const kind = getConversationKind(conversation);
  return kind === 'external-direct' || kind === 'external-channel';
};

export const isRegularDirectConversation = (conversation: ConversationClassificationSource): boolean => {
  return getConversationKind(conversation) === 'internal-direct';
};

export const isRegularChannelConversation = (conversation: ConversationClassificationSource): boolean => {
  return getConversationKind(conversation) === 'internal-channel';
};

export const matchesConversationNavSection = (conversation: ConversationClassificationSource, navSection: NavSection): boolean => {
  const customListId = getCustomListIdFromNavSection(navSection);
  if (customListId) {
    return conversation.customListId === customListId;
  }

  if (navSection === 'direct-messages') {
    return isRegularDirectConversation(conversation);
  }

  if (navSection === 'channels') {
    return isRegularChannelConversation(conversation);
  }

  if (navSection === 'external') {
    return isExternalConversation(conversation);
  }

  return true;
};